import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { clearSession, logoutUser } from "./sessionSlice";

interface AdminUser {
  uid: string;
  name: string | null;
  role: string;
  id: number;
  avatar: string | null;
}

interface ImpersonationState {
  isImpersonating: boolean;
  targetUid: string | null;
  adminUser: AdminUser | null;
}

const STORAGE_KEY = "dmy_impersonation";

function loadFromSessionStorage(): Partial<ImpersonationState> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.targetUid && parsed.adminUser) {
        return {
          isImpersonating: true,
          targetUid: parsed.targetUid,
          adminUser: parsed.adminUser,
        };
      }
    }
  } catch {
    sessionStorage.removeItem(STORAGE_KEY);
  }
  return {};
}

const persisted = loadFromSessionStorage();

const initialState: ImpersonationState = {
  isImpersonating: persisted.isImpersonating ?? false,
  targetUid: persisted.targetUid ?? null,
  adminUser: persisted.adminUser ?? null,
};

const impersonationSlice = createSlice({
  name: "impersonation",
  initialState,
  reducers: {
    startImpersonation(
      state,
      action: PayloadAction<{ targetUid: string; adminUser: AdminUser }>
    ) {
      state.isImpersonating = true;
      state.targetUid = action.payload.targetUid;
      state.adminUser = action.payload.adminUser;
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          targetUid: action.payload.targetUid,
          adminUser: action.payload.adminUser,
        })
      );
    },
    stopImpersonation(state) {
      state.isImpersonating = false;
      state.targetUid = null;
      state.adminUser = null;
      sessionStorage.removeItem(STORAGE_KEY);
    },
  },
  extraReducers: (builder) => {
    // Auto-clear impersonation when session is cleared (401, logout, etc.)
    const clearImpersonation = (state: ImpersonationState) => {
      state.isImpersonating = false;
      state.targetUid = null;
      state.adminUser = null;
      sessionStorage.removeItem(STORAGE_KEY);
    };
    builder.addCase(clearSession, clearImpersonation);
    builder.addCase(logoutUser, clearImpersonation);
  },
});

export const { startImpersonation, stopImpersonation } =
  impersonationSlice.actions;
export default impersonationSlice.reducer;
